
import Vue from "vue";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle/TempStarsTableTitle.vue";
import {
  getPartnerReferralCodes,
  createPartnerReferralCode,
  updatePartnerReferralCode,
  deletePartnerReferralCode,
  getMandrillTemplates,
  PartnerReferralCode,
  PromotionType
} from "@/api";

interface FormData {
  partner_name: string;
  referral_code: string;
  promotion_type: PromotionType;
  free_days: number;
  free_placements: number;
  active_duration_days: number | null;
  email_template: string | null;
  valid_from: string | null;
  valid_until: string | null;
  notes: string | null;
}

export default Vue.extend({
  name: "PartnerReferralCodes",
  
  components: {
    TempStarsTableTitle
  },
  
  data() {
    return {
      // Table data
      codes: [] as PartnerReferralCode[],
      loading: false,
      search: "",
      error: "",
      
      // Dialog state
      dialog: false,
      editMode: false,
      editingId: null as number | null,
      formValid: false,
      saving: false,
      
      // Delete dialog
      deleteDialog: false,
      deleteItem: null as PartnerReferralCode | null,
      deleting: false,
      
      // Date picker menus
      validFromMenu: false,
      validUntilMenu: false,
      
      // Email templates
      emailTemplates: [] as { slug: string; label: string }[],
      loadingTemplates: false,
      
      // Promotion type dropdown options
      promotionTypeOptions: [
        { text: "Free Time Period", value: "free_time_period" },
        { text: "Free Placements", value: "free_placements" }
      ],
      
      // Form data
      formData: {
        partner_name: "",
        referral_code: "",
        promotion_type: "free_time_period",
        free_days: 90,
        free_placements: 1,
        active_duration_days: null,
        email_template: null,
        valid_from: null,
        valid_until: null,
        notes: null
      } as FormData,
      
      // Snackbar
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      
      // Validation rules
      rules: {
        required: (v: any) => !!v || "This field is required",
        positiveNumber: (v: any) => (v && v > 0) || "Must be a positive number",
        noSpaces: (v: string) => !v || !/\s/.test(v) || "No spaces allowed"
      },
      
      // Table headers
      headers: [
        { text: "ID", value: "id", width: "70px" },
        { text: "Partner Name", value: "partner_name" },
        { text: "Referral Code", value: "referral_code" },
        { text: "Promo Type", value: "promotion_type", width: "140px" },
        { text: "Promo Value", value: "promo_value", width: "130px", sortable: false },
        { text: "Status", value: "status", width: "120px", sortable: false },
        { text: "Valid From", value: "valid_from", width: "120px" },
        { text: "Valid Until", value: "valid_until", width: "120px" },
        { text: "Notes", value: "notes" },
        { text: "Created", value: "created_at", width: "150px" },
        { text: "Actions", value: "actions", sortable: false, width: "100px", align: "center" }
      ]
    };
  },
  
  async created() {
    await Promise.all([
      this.loadCodes(),
      this.loadEmailTemplates()
    ]);
  },
  
  methods: {
    /**
     * Load available Mandrill email templates for the dropdown
     */
    async loadEmailTemplates(): Promise<void> {
      this.loadingTemplates = true;
      try {
        const templates = await getMandrillTemplates();
        this.emailTemplates = templates.map(t => ({
          slug: t.slug,
          label: `${t.name} (${t.slug})`
        }));
      } catch {
        this.emailTemplates = [];
      } finally {
        this.loadingTemplates = false;
      }
    },
    
    /**
     * Load all partner referral codes from the API
     */
    async loadCodes(): Promise<void> {
      this.loading = true;
      this.error = "";
      
      try {
        this.codes = await getPartnerReferralCodes();
      } catch (err) {
        const error = err as Error;
        this.error = error.message || "Failed to load partner referral codes";
        this.showSnackbar(this.error, "error");
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * Open the dialog for creating a new code
     */
    openCreateDialog(): void {
      this.editMode = false;
      this.editingId = null;
      this.resetForm();
      this.dialog = true;
    },
    
    /**
     * Open the dialog for editing an existing code
     */
    openEditDialog(item: PartnerReferralCode): void {
      this.editMode = true;
      this.editingId = item.id;
      this.formData = {
        partner_name: item.partner_name,
        referral_code: item.referral_code,
        promotion_type: item.promotion_type,
        free_days: item.free_days || 90,
        free_placements: item.free_placements || 1,
        active_duration_days: item.active_duration_days,
        email_template: item.email_template,
        valid_from: item.valid_from ? item.valid_from.split("T")[0] : null,
        valid_until: item.valid_until ? item.valid_until.split("T")[0] : null,
        notes: item.notes
      };
      this.dialog = true;
    },
    
    /**
     * Close the create/edit dialog
     */
    closeDialog(): void {
      this.dialog = false;
      this.resetForm();
    },
    
    /**
     * Reset form to default values
     */
    resetForm(): void {
      this.formData = {
        partner_name: "",
        referral_code: "",
        promotion_type: "free_time_period",
        free_days: 90,
        free_placements: 1,
        active_duration_days: null,
        email_template: null,
        valid_from: null,
        valid_until: null,
        notes: null
      };
      
      // Reset validation
      if (this.$refs.form) {
        (this.$refs.form as any).resetValidation();
      }
    },
    
    /**
     * Save the code (create or update)
     */
    async saveCode(): Promise<void> {
      if (!this.formValid) return;
      
      this.saving = true;
      
      try {
        const common = {
          partner_name: this.formData.partner_name,
          referral_code: this.formData.referral_code.toUpperCase(),
          active_duration_days: this.formData.active_duration_days || null,
          email_template: this.formData.email_template || null,
          valid_from: this.formData.valid_from || null,
          valid_until: this.formData.valid_until || null,
          notes: this.formData.notes || null
        };

        const promoFields = this.formData.promotion_type === 'free_time_period'
          ? { promotion_type: 'free_time_period' as const, free_days: this.formData.free_days }
          : { promotion_type: 'free_placements' as const, free_placements: this.formData.free_placements };

        const payload = { ...common, ...promoFields };
        
        if (this.editMode && this.editingId) {
          await updatePartnerReferralCode(this.editingId, payload);
          this.showSnackbar("Partner referral code updated successfully", "success");
        } else {
          await createPartnerReferralCode(payload);
          this.showSnackbar("Partner referral code created successfully", "success");
        }
        
        this.closeDialog();
        await this.loadCodes();
      } catch (err) {
        const error = err as Error;
        this.showSnackbar(error.message || "Failed to save partner referral code", "error");
      } finally {
        this.saving = false;
      }
    },
    
    /**
     * Show delete confirmation dialog
     */
    confirmDelete(item: PartnerReferralCode): void {
      this.deleteItem = item;
      this.deleteDialog = true;
    },
    
    /**
     * Delete the selected code
     */
    async deleteCode(): Promise<void> {
      if (!this.deleteItem) return;
      
      this.deleting = true;
      
      try {
        await deletePartnerReferralCode(this.deleteItem.id);
        this.showSnackbar("Partner referral code deleted successfully", "success");
        this.deleteDialog = false;
        this.deleteItem = null;
        await this.loadCodes();
      } catch (err) {
        const error = err as Error;
        this.showSnackbar(error.message || "Failed to delete partner referral code", "error");
      } finally {
        this.deleting = false;
      }
    },
    
    /**
     * Get status color based on validity dates
     */
    getStatusColor(item: PartnerReferralCode): string {
      const now = new Date();
      
      if (item.valid_from && new Date(item.valid_from) > now) {
        return "orange"; // Not yet active
      }
      
      if (item.valid_until && new Date(item.valid_until) < now) {
        return "grey"; // Expired
      }
      
      return "green"; // Active
    },
    
    /**
     * Get status text based on validity dates
     */
    getStatusText(item: PartnerReferralCode): string {
      const now = new Date();
      
      if (item.valid_from && new Date(item.valid_from) > now) {
        return "Pending";
      }
      
      if (item.valid_until && new Date(item.valid_until) < now) {
        return "Expired";
      }
      
      return "Active";
    },
    
    /**
     * Format date for display
     */
    formatDate(dateStr: string | null): string {
      if (!dateStr) return "-";
      
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      } catch {
        return dateStr;
      }
    },
    
    /**
     * Format datetime for display
     */
    formatDateTime(dateStr: string | null): string {
      if (!dateStr) return "-";
      
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        });
      } catch {
        return dateStr;
      }
    },
    
    /**
     * Truncate text with ellipsis
     */
    truncateText(text: string, maxLength: number): string {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },
    
    /**
     * Show snackbar notification
     */
    showSnackbar(text: string, color: string): void {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  }
});
